import {
    Component,
    effect,
    EventEmitter,
    inject,
    Input,
    Output,
    signal
} from "@angular/core";
import { PeriodNames } from "@arch/_shared/types/interval-dates";
import { ScenarioEntity } from "@arch/domain/entity/scenario.entity";
import { DeviceService } from "@shared/services/device.service";
import { SharedMaterialModule } from "@shared/modules/shared-material.module";
import { SharedDsnModule } from "@shared/modules/shared-dsn.module";
import {
    DatePeriodService,
    FormPeriod,
    PeriodLabel,
    Periods
} from "@shared/services/date-period.service";
import { DateUtils } from "@arch/_shared/utils";

interface NavigationType {
    label: string;
    value: PeriodNames;
}

@Component({
    selector: "app-available-dates-selector",
    imports: [SharedMaterialModule, SharedDsnModule],
    templateUrl: "./app-available-dates-selector.component.html",
    styleUrl: "./app-available-dates-selector.component.scss",
    standalone: true
})
export class AppAvailableDatesSelectorComponent {
    @Input({ required: true }) set availableDates(value: Date[]) {
        this.availableDatesSignal.set(value);
    }
    private availableDatesSignal = signal<Date[]>([]);
    get availableDates() {
        return this.availableDatesSignal();
    }
    @Input({ required: true }) set scenario(value: ScenarioEntity) {
        this.scenarioSignal.set(value);
    }
    private scenarioSignal = signal<ScenarioEntity>(null);
    get scenario() {
        return this.scenarioSignal();
    }
    @Input({ required: true }) startDate!: Date;
    @Input({ required: true }) endDate!: Date;
    @Input({ required: false }) allMonthsCompare!: boolean;
    @Input({ required: true }) periodName!: PeriodNames;
    @Input({ required: true }) scenarios!: ScenarioEntity[];
    @Input({ required: true }) typeSelect: "period" | "comparation" = "period";
    @Input({ required: false }) disabledStartDate: boolean = false;
    @Input({ required: false }) disabledScenario: boolean = false;
    @Input({ required: false }) configs: {
        equalYear: boolean;
        fixedActions?: boolean;
    } = { equalYear: false, fixedActions: false };
    @Output() handleApply = new EventEmitter<{
        scenario: ScenarioEntity;
        periodName: PeriodNames;
        startDate: Date;
        endDate: Date;
        allMonthsCompare?: boolean;
    }>();
    @Output() handleSelectScenario = new EventEmitter<ScenarioEntity>();
    @Output() handleResetPeriod = new EventEmitter<void>();

    public readonly datePeriodService = inject(DatePeriodService);
    private readonly deviceService = inject(DeviceService);

    public readonly navigationPeriods: NavigationType[] = [
        { label: "Mensal", value: "monthly" },
        { label: "Trimestral", value: "quarterly" },
        { label: "Semestral", value: "semester" },
        { label: "Anual", value: "yearly" }
    ];
    public readonly isMobile = this.deviceService.getIsMobile();
    public formPeriod!: FormPeriod;
    public periods: Periods;
    public periodsEnd: Periods;

    constructor() {
        effect(() => {
            if (!this.scenario) return;
            this.initializeFormPeriod();
            this.periods = this.datePeriodService.getPeriods();

            this.periodsEnd = this.datePeriodService.getPeriodsEnd();
            this.updatePeriodsDisable();
        });
    }

    private initializeFormPeriod(): void {
        this.formPeriod = {
            scenario: this.scenario,
            periodName: this.periodName,
            allMonthsCompare: this.allMonthsCompare,
            start: {
                year: this.startDate.getUTCFullYear(),
                month: this.startDate.getUTCMonth() + 1,
                date: null
            },
            end: {
                year: this.endDate.getUTCFullYear(),
                month: this.endDate.getUTCMonth() + 1,
                date: null
            }
        };
    }

    private updatePeriodsDisable(): void {
        this.datePeriodService.updatePeriodsDisable(
            this.availableDates,
            this.formPeriod
        );
    }

    public onNavigate(page: NavigationType): void {
        const monthsByPeriod = {
            monthly: DateUtils.getPeriodConfig("monthly"),
            quarterly: DateUtils.getPeriodConfig("quarterly"),
            semester: DateUtils.getPeriodConfig("semester"),
            yearly: DateUtils.getPeriodConfig("yearly")
        };
        if (this.formPeriod.periodName !== page.value) {
            this.formPeriod.periodName = page.value;
            if (this.configs?.equalYear) {
                const months = monthsByPeriod[this.formPeriod.periodName];
                this.formPeriod.start.month = months[0];
                this.formPeriod.end.month = months[months.length - 1];
                this.updatePeriodsDisable();
            } else {
                const { start, end } =
                    this.datePeriodService.selectInitialDates(
                        this.availableDates,
                        this.formPeriod.periodName!
                    );
                this.formPeriod.start.year = start.year;
                this.formPeriod.start.month = start.month;
                this.formPeriod.end.year = end.year;
                this.formPeriod.end.month = end.month;
                this.updatePeriodsDisable();
            }
        }
    }

    public onSelectPeriodYear(
        period: PeriodLabel,
        type: "start" | "end"
    ): void {
        if (this.configs?.equalYear) {
            this.formPeriod.start.year = period.value;
            this.formPeriod.end.year = period.value;
        } else {
            this.formPeriod[type].year = period.value;
        }
        this.updatePeriodsDisable();
    }

    public onSelectPeriodMonth(
        period: PeriodLabel,
        type: "start" | "end"
    ): void {
        this.formPeriod.allMonthsCompare = false;
        const typesPeriodSelected: Record<string, Record<number, number>> = {
            quarterly: { 1: 3, 2: 6, 3: 9, 4: 12 },
            semester: { 1: 6, 2: 12 }
        };
        const typesPeriod: Record<string, number> = {
            quarterly: 3,
            semester: 6
        };
        if (typesPeriodSelected[this.formPeriod.periodName!]) {
            const tempPeriod =
                typesPeriodSelected[this.formPeriod.periodName!][
                    Math.ceil(
                        period.value / typesPeriod[this.formPeriod.periodName!]
                    )
                ];
            this.formPeriod[type].month = tempPeriod;
        } else {
            this.formPeriod[type].month = period.value;
        }
        this.setDate(type);
    }

    private setDate(type: "start" | "end"): void {
        try {
            this.formPeriod[type].date = this.datePeriodService.createDate(
                type,
                this.formPeriod,
                this.availableDates
            );
        } catch (error) {
            console.error("Error setting date:", error);
        }
    }

    public onApplyPeriod(): void {
        try {
            this.formPeriod.start.date = this.datePeriodService.createDate(
                "start",
                this.formPeriod,
                this.availableDates
            );
            this.formPeriod.end.date = this.datePeriodService.createDate(
                "end",
                this.formPeriod,
                this.availableDates
            );
            this.handleApply.emit({
                scenario: this.scenario,
                periodName: this.formPeriod.periodName,
                startDate: this.formPeriod.start.date,
                endDate: this.formPeriod.end.date,
                allMonthsCompare: this.formPeriod.allMonthsCompare
            });
        } catch (error) {
            console.error("Error applying period:", error);
        }
    }

    public onResetPeriod(): void {
        this.handleResetPeriod.emit();
    }

    public onSelectScenario(scenario: ScenarioEntity): void {
        this.handleSelectScenario.emit(scenario);
        setTimeout(() => {
            const { start, end } = this.datePeriodService.selectInitialDates(
                this.availableDates,
                this.formPeriod.periodName!
            );
            this.formPeriod.start.year = start.year;
            this.formPeriod.start.month = start.month;
            this.formPeriod.end.year = end.year;
            this.formPeriod.end.month = end.month;
            this.updatePeriodsDisable();
        }, 500);
    }

    public trackByYear(index: number, year: PeriodLabel): number {
        return year.value;
    }

    public trackByMonth(index: number, month: PeriodLabel): number {
        return month.value;
    }

    public trackByNavigation(index: number, nav: NavigationType): string {
        return nav.value;
    }

    public isDisabled(): boolean {
        //if (this.hasNoSelectedPeriod()) return true;
        return (
            !this.formPeriod.start.month ||
            !this.formPeriod.end.month ||
            !this.formPeriod.start.year ||
            !this.formPeriod.end.year
        );
    }

    public onSelectAllMonthsCompare(): void {
        this.formPeriod.allMonthsCompare = true;
        this.formPeriod.start.month = 12;
        this.formPeriod.end.month = 12;
    }
}
